import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { ContractType } from "./admin-clients.js";
import { NotFound } from "./folders.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { ExaminationFollowUpPeriodSchema, LanguageSchema, csvOf, } from "./column-schemas.js";
/**
 * Precautionary-index examinations administration: the `examinations` table
 * plus its two translation taxonomies (`examination_types`,
 * `examination_abbreviations`) and the `examination_shares` read model.
 * Gated to OHS Admin/AMA only — SiFa and external SiFa have no examinations
 * tab in `apps/web` (`admin/[id]/page.tsx`'s own `!isSifa && !isExternalSifa`
 * gate), unlike the broader `isUnrestrictedInternal` set the clients/
 * locations/documents/online-courses groups admit.
 */
// ---------------------------------------------------------------------------
// examination-types — a translatable taxonomy row per language; rows in the
// same `translation_group_id` are the language variants of one concept. A
// create that omits `translation_group_id` gets one minted by the
// `ensure_translation_group` DB trigger, the same "server computes it" rule
// `admin-folders`' depth/materialized_path follows.
// ---------------------------------------------------------------------------
export const ExaminationTypeRow = S.Struct({
    id: S.Int,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    value: S.String,
    date_created: S.NullOr(S.String),
});
export const ListExaminationTypesParams = S.Struct({
    language: S.optional(LanguageSchema),
});
export const ListExaminationTypes = HttpApiEndpoint.get("listExaminationTypes", "/admin/examinations/types")
    .setUrlParams(ListExaminationTypesParams)
    .addSuccess(S.Struct({ items: S.Array(ExaminationTypeRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const CreateExaminationTypePayload = S.Struct({
    value: S.NonEmptyString,
    language: LanguageSchema,
    translation_group_id: S.optional(S.Int),
});
export const CreateExaminationType = HttpApiEndpoint.post("createExaminationType", "/admin/examinations/types/create")
    .setPayload(CreateExaminationTypePayload)
    .addSuccess(S.Struct({ data: ExaminationTypeRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateExaminationTypePayload = S.Struct({
    id: S.Int,
    value: S.NonEmptyString,
});
export const UpdateExaminationType = HttpApiEndpoint.post("updateExaminationType", "/admin/examinations/types/update")
    .setPayload(UpdateExaminationTypePayload)
    .addSuccess(S.Struct({ data: ExaminationTypeRow }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteExaminationTypePayload = S.Struct({ id: S.Int });
export const DeleteExaminationType = HttpApiEndpoint.post("deleteExaminationType", "/admin/examinations/types/delete")
    .setPayload(DeleteExaminationTypePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// examination-abbreviations — identical shape/rules to examination-types,
// a separate taxonomy table.
// ---------------------------------------------------------------------------
export const ExaminationAbbreviationRow = S.Struct({
    id: S.Int,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    value: S.String,
    date_created: S.NullOr(S.String),
});
export const ListExaminationAbbreviationsParams = S.Struct({
    language: S.optional(LanguageSchema),
});
export const ListExaminationAbbreviations = HttpApiEndpoint.get("listExaminationAbbreviations", "/admin/examinations/abbreviations")
    .setUrlParams(ListExaminationAbbreviationsParams)
    .addSuccess(S.Struct({ items: S.Array(ExaminationAbbreviationRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const CreateExaminationAbbreviationPayload = S.Struct({
    value: S.NonEmptyString,
    language: LanguageSchema,
    translation_group_id: S.optional(S.Int),
});
export const CreateExaminationAbbreviation = HttpApiEndpoint.post("createExaminationAbbreviation", "/admin/examinations/abbreviations/create")
    .setPayload(CreateExaminationAbbreviationPayload)
    .addSuccess(S.Struct({ data: ExaminationAbbreviationRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateExaminationAbbreviationPayload = S.Struct({
    id: S.Int,
    value: S.NonEmptyString,
});
export const UpdateExaminationAbbreviation = HttpApiEndpoint.post("updateExaminationAbbreviation", "/admin/examinations/abbreviations/update")
    .setPayload(UpdateExaminationAbbreviationPayload)
    .addSuccess(S.Struct({ data: ExaminationAbbreviationRow }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteExaminationAbbreviationPayload = S.Struct({ id: S.Int });
export const DeleteExaminationAbbreviation = HttpApiEndpoint.post("deleteExaminationAbbreviation", "/admin/examinations/abbreviations/delete")
    .setPayload(DeleteExaminationAbbreviationPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// examinations — reads come from `vw_examinations` (joins in the taxonomy
// tables' `value` for the caller's convenience, matching apps/web's
// `ExaminationView`); writes land on the base `examinations` table.
// ---------------------------------------------------------------------------
export const FollowUpPeriod = S.Literal("monthly", "quarterly", "6-months", "yearly", "2-yearly", "3-yearly");
export const ExaminationRow = S.Struct({
    id: S.UUID,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    title: S.String,
    description: S.String,
    type_translation_group_id: S.NullOr(S.Int),
    type_value: S.NullOr(S.String),
    abbreviation_translation_group_id: S.NullOr(S.Int),
    abbreviation_value: S.NullOr(S.String),
    follow_up_period: S.NullOr(FollowUpPeriod),
    no_follow_up_required: S.Boolean,
    thumbnail: S.NullOr(S.UUID),
    contract_types: S.NullOr(S.Array(ContractType)),
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
const StringCsvList = S.transform(S.String, S.Array(S.String), {
    strict: true,
    decode: (value) => value.split(",").filter((item) => item.length > 0),
    encode: (values) => values.join(","),
});
export const ListExaminationsParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
    translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
    type_values: S.optional(StringCsvList),
    follow_up_periods: S.optional(csvOf(ExaminationFollowUpPeriodSchema)),
}));
export const ListExaminations = HttpApiEndpoint.get("listExaminations", "/admin/examinations")
    .setUrlParams(ListExaminationsParams)
    .addSuccess(pagedResponse(ExaminationRow))
    .addError(Forbidden, { status: 403 });
export const GetExaminationParams = S.Struct({ id: S.UUID });
export const GetExamination = HttpApiEndpoint.get("getExamination", "/admin/examinations/get")
    .setUrlParams(GetExaminationParams)
    .addSuccess(S.Struct({ data: ExaminationRow }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const CreateExaminationPayload = S.Struct({
    title: S.NonEmptyString,
    description: S.NonEmptyString,
    language: S.optionalWith(LanguageSchema, {
        default: () => "de-DE",
    }),
    translation_group_id: S.optional(S.Int),
    type_translation_group_id: S.optional(S.Int),
    abbreviation_translation_group_id: S.optional(S.Int),
    follow_up_period: S.optional(FollowUpPeriod),
    no_follow_up_required: S.optional(S.Boolean),
    thumbnail: S.optional(S.UUID),
    contract_types: S.optional(S.Array(ContractType)),
});
export const CreateExamination = HttpApiEndpoint.post("createExamination", "/admin/examinations/create")
    .setPayload(CreateExaminationPayload)
    .addSuccess(S.Struct({ data: ExaminationRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateExaminationPayload = S.Struct({
    id: S.UUID,
    title: S.optional(S.NonEmptyString),
    description: S.optional(S.NonEmptyString),
    type_translation_group_id: S.optional(S.NullOr(S.Int)),
    abbreviation_translation_group_id: S.optional(S.NullOr(S.Int)),
    follow_up_period: S.optional(S.NullOr(FollowUpPeriod)),
    no_follow_up_required: S.optional(S.Boolean),
    thumbnail: S.optional(S.NullOr(S.UUID)),
    contract_types: S.optional(S.NullOr(S.Array(ContractType))),
});
export const UpdateExamination = HttpApiEndpoint.post("updateExamination", "/admin/examinations/update")
    .setPayload(UpdateExaminationPayload)
    .addSuccess(S.Struct({ data: ExaminationRow }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteExaminationPayload = S.Struct({ id: S.UUID });
export const DeleteExamination = HttpApiEndpoint.post("deleteExamination", "/admin/examinations/delete")
    .setPayload(DeleteExaminationPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Retires every language variant of one examination at once — the
 * examinations screen's bulk delete on an edit-both-languages flow,
 * mirroring `admin-risk-instances.deleteRiskInstancesByTranslationGroup`.
 */
export const DeleteExaminationsByTranslationGroupPayload = S.Struct({
    translation_group_id: S.Int,
});
export const DeleteExaminationsByTranslationGroup = HttpApiEndpoint.post("deleteExaminationsByTranslationGroup", "/admin/examinations/delete-by-translation-group")
    .setPayload(DeleteExaminationsByTranslationGroupPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Int }) }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// examination-shares — the same client-level "shared with" read model
// `admin-documents` exposes for files/folders, here for `examination_shares`.
// ---------------------------------------------------------------------------
/** A client-level examination share, resolved to the recipient client's name. */
export const SharedClientRow = S.Struct({
    client_id: S.UUID,
    client_name: S.NullOr(S.String),
});
export const ListExaminationSharesParams = S.Struct({
    examination_id: S.UUID,
});
export const ListExaminationShares = HttpApiEndpoint.get("listExaminationShares", "/admin/examinations/shares")
    .setUrlParams(ListExaminationSharesParams)
    .addSuccess(S.Struct({ items: S.Array(SharedClientRow) }))
    .addError(Forbidden, { status: 403 });
export const adminExaminationsGroup = HttpApiGroup.make("admin-examinations")
    .add(ListExaminationTypes)
    .add(CreateExaminationType)
    .add(UpdateExaminationType)
    .add(DeleteExaminationType)
    .add(ListExaminationAbbreviations)
    .add(CreateExaminationAbbreviation)
    .add(UpdateExaminationAbbreviation)
    .add(DeleteExaminationAbbreviation)
    .add(ListExaminations)
    .add(GetExamination)
    .add(CreateExamination)
    .add(UpdateExamination)
    .add(DeleteExamination)
    .add(DeleteExaminationsByTranslationGroup)
    .add(ListExaminationShares)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=examinations.js.map